"use client";

import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import type { Category, ComboV3, Product } from "@/src/types/tenant";
import type { FilterState } from "./SidebarFilter";
import { matchesPriceRange } from "@/src/utils/filter/productFilter";
import {
  getSubcategoryTrees,
  type SubcategoryNode,
} from "@/src/services/categories/subcategoryTree.service";
import type { ProductFacets } from "@/src/services/product/productPage.service";
import { toast } from "sonner";
import { useServiceArea } from "@/src/hooks/serviceArea/useServiceArea";

interface CanonicalNode {
  _id: string;
  name: string;
  level: number;
  productCount?: number;
  productIds?: string[];
  display?: { tileImageUrl?: string };
  children?: CanonicalNode[];
}

interface CanonicalNode {
  _id: string;
  name: string;
  level: number;
  productCount?: number;
  productIds?: string[];
  display?: { tileImageUrl?: string };
  children?: CanonicalNode[];
}

interface CategoryFilterBarProps {
  categories?: Category[];
  products?: Product[];
  combos?: ComboV3[];
  onFilterChange?: (filters: FilterState) => void;
  /**
   * Catalogue-wide counts from `GET /api/products/facets`, scoped to the outlet
   * ONLY (no category / price / tag narrowing). Supplied by a server-paged
   * caller, whose `products` prop holds just the current page and therefore
   * cannot be counted over. When omitted every count and the curator tag list
   * are computed in memory from `products`, exactly as before — that keeps the
   * home page and any other caller unchanged.
   */
  facets?: ProductFacets | null;
  /**
   * Facets narrowed to the currently selected group (category or department).
   * Only used to enumerate the subcategory row for sellers with a FLAT taxonomy,
   * where the leaf list would otherwise have to be derived from the full
   * catalogue. Department-based sellers get their leaves from the nested tree.
   */
  groupFacets?: ProductFacets | null;
  /** Pre-selected group id (e.g. from `?category=` on the URL), or "all". */
  initialCategory?: string;
  /** Scopes the category artwork lookup to this outlet's own catalogue. */
  outletId?: string;
  /**
   * Every landing outlet, when the store has more than one. The canonical tree
   * drives the category filter's `productIds` predicate, so asking for a single
   * branch would hide categories — and filter out products — that another
   * branch stocks. Falls back to `outletId` when absent.
   */
  outletIds?: string[];
}

const PRICE_RANGES = [
  { id: "under-300", label: "Under ₹300" },
  { id: "300-600", label: "₹300 – ₹600" },
  { id: "600-1000", label: "₹600 – ₹1,000" },
  { id: "above-1000", label: "Above ₹1,000" },
];

const CURATORS = [
  { id: "bestsellers", label: "Bestsellers" },
  { id: "new", label: "New arrivals" },
  { id: "gifting", label: "Gifting" },
  { id: "sale", label: "On sale" },
];

function effectivePrice(p: Product): number {
  return p.pricing?.offerPrice ?? p.pricing?.sellingPrice ?? 0;
}

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: open ? "rotate(180deg)" : "none",
      transition: "transform 180ms",
      opacity: 0.6,
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export const CategoryFilterBar: React.FC<CategoryFilterBarProps> = ({
  categories = [],
  products = [],
  combos = [],
  onFilterChange,
  facets = null,
  groupFacets = null,
  initialCategory = "all",
  outletId,
  outletIds,
}) => {
  /* Outlets that only deliver to their own city (Farm Fresh, Jyoti Organic …).
     The section still renders for an out-of-area buyer — hiding it would make the
     storefront look empty and give no reason why — but nothing in it responds,
     and a click says why. Same `blocked` signal the notice and the add-to-cart
     guard already use, so all three agree by construction. */
  const { area: serviceArea, blocked: serviceBlocked } =
    useServiceArea(outletId);

  const [activeCategory, setActiveCategory] = useState(
    initialCategory || "all",
  );
  const [activeSubcategory, setActiveSubcategory] = useState("all");
  const [curators, setCurators] = useState<string[]>([]);
  const [priceRanges, setPriceRanges] = useState<string[]>([]);
  const [openMenu, setOpenMenu] = useState<"curator" | "price" | null>(null);

  const curatorRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLDivElement>(null);

  const emit = useCallback(
    (
      cat: string,
      sub: string,
      cur: string[],
      prices: string[],
      productIds?: string[],
    ) => {
      onFilterChange?.({
        category: cat,
        subcategory: sub,
        curators: cur,
        priceRanges: prices,
        categoryProductIds: productIds,
      });
    },
    [onFilterChange],
  );

  // Close dropdowns on outside click / Escape
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (curatorRef.current?.contains(t) || priceRef.current?.contains(t))
        return;
      setOpenMenu(null);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpenMenu(null);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const handleCategory = (id: string) => {
    setActiveCategory(id);
    setActiveSubcategory("all");
    emit(id, "all", curators, priceRanges);
  };

  /** Select a canonical category, carrying its product membership. */
  const handleCategoryWithIds = (id: string, productIds: string[]) => {
    setActiveCategory(id);
    setActiveSubcategory("all");
    emit(id, "all", curators, priceRanges, productIds);
  };
  const handleSubcategory = (subId: string) => {
    setActiveSubcategory(subId);
    emit(activeCategory, subId, curators, priceRanges);
  };
  const toggleCurator = (id: string) => {
    const next = curators.includes(id)
      ? curators.filter((c) => c !== id)
      : [...curators, id];
    setCurators(next);
    emit(activeCategory, activeSubcategory, next, priceRanges);
  };
  const togglePrice = (id: string) => {
    const next = priceRanges.includes(id)
      ? priceRanges.filter((p) => p !== id)
      : [...priceRanges, id];
    setPriceRanges(next);
    emit(activeCategory, activeSubcategory, curators, next);
  };
  const clearAll = () => {
    setCurators([]);
    setPriceRanges([]);
    emit(activeCategory, activeSubcategory, [], []);
  };

  // ── Derived data (mirrors SidebarFilter) ──────────────────────────────────
  const availableTags = useMemo(() => {
    const set = new Set<string>();
    products.forEach((p) => p.tags?.forEach((t) => t && set.add(t)));
    combos.forEach((c) => c.tag && set.add(c.tag));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [products, combos]);

  const curatorItems = useMemo(
    () =>
      availableTags.length > 0
        ? availableTags.map((t) => ({ id: t, label: t }))
        : CURATORS,
    [availableTags],
  );

  const subcategoryMap = useMemo(() => {
    const map = new Map<string, Map<string, Category>>();
    products.forEach((p) => {
      if (!p.category || !p.subcategories?.length) return;
      const catId = p.category._id;
      if (!map.has(catId)) map.set(catId, new Map());
      p.subcategories.forEach((sc) => map.get(catId)!.set(sc._id, sc));
    });
    return map;
  }, [products]);

  // Every subcategory id this catalogue actually uses. Products carry the
  // department AND the leaf in one flat array with no parent pointer, so the
  // hierarchy has to be joined in from portal-api.
  const usedSubcategoryIds = useMemo(() => {
    const set = new Set<string>();
    products.forEach((p) =>
      p.subcategories?.forEach((sc) => set.add(String(sc._id))),
    );
    return set;
  }, [products]);

  const [trees, setTrees] = useState<Map<string, SubcategoryNode[]>>(new Map());

  /**
   * Category artwork, keyed by the legacy portal-api id the pills already use.
   *
   * The landing payload this bar renders from carries only legacy ids and no
   * `taxonomy` block, so it cannot look artwork up by the product service's own
   * ids. `/api/categories/legacy-image-map` does that translation server-side
   * and hands back a plain id -> image map. Failure is silent: no map simply
   * means the tile row does not render and the pills behave exactly as before.
   */
  /**
   * The canonical browse tree for this outlet, with the product ids under each
   * node.
   *
   * This is the storefront's own taxonomy source, independent of portal-api.
   * It carries artwork and categories portal-api has no concept of (Anuttama's
   * 18 chocolates are one portal-api subcategory but five canonical ones), and
   * the ids let us filter without portal-api ever needing to know about them.
   * On any failure we simply fall back to the legacy pills.
   */
  const [canonical, setCanonical] = useState<CanonicalNode[]>([]);

  useEffect(() => {
    const root = (process.env.NEXT_PUBLIC_PRODUCT_SERVICE_URL || "").replace(
      /\/+$/,
      "",
    );
    const ids = outletIds?.length ? outletIds : outletId ? [outletId] : [];
    if (!root || !ids.length) return;
    let cancelled = false;
    fetch(
      `${root}/categories/tree?outletIds=${encodeURIComponent(ids.join(","))}&hideEmpty=true&withProductIds=true`,
      { cache: "no-store", headers: { accept: "application/json" } },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (!cancelled && Array.isArray(body?.data)) setCanonical(body.data);
      })
      .catch((err) =>
        console.error("canonical category tree load failed:", err),
      );
    return () => {
      cancelled = true;
    };
  }, [outletId, outletIds]);

  useEffect(() => {
    const ids = categories.map((c) => c._id).filter(Boolean);
    if (!ids.length) return;
    let cancelled = false;
    getSubcategoryTrees(ids).then((t) => {
      if (!cancelled) setTrees(t);
    });
    return () => {
      cancelled = true;
    };
  }, [categories]);

  /**
   * Departments this catalogue actually stocks, flattened across every platform
   * category, each with only the leaves that have products. When a seller models
   * their nav this way we promote departments to the primary pill row so the
   * storefront reads like their own site; sellers with a flat taxonomy produce an
   * empty list here and keep the original category pills.
   */
  const departments = useMemo(() => {
    const out: {
      id: string;
      label: string;
      leaves: { id: string; label: string }[];
    }[] = [];
    for (const cat of categories) {
      for (const node of trees.get(String(cat._id)) ?? []) {
        const id = String(node.id);
        const leaves = (node.children ?? [])
          .map((ch) => ({ id: String(ch.id), label: ch.name }))
          .filter((ch) => usedSubcategoryIds.has(ch.id));
        // A department earns a pill if it, or any of its leaves, is stocked.
        if (usedSubcategoryIds.has(id) || leaves.length) {
          out.push({ id, label: node.name, leaves });
        }
      }
    }
    return out;
  }, [categories, trees, usedSubcategoryIds]);

  const useDepartments = departments.length > 0;

  /** Matches the widened predicate in ShopClient: a group id may be a category OR a subcategory. */
  const countForGroup = (groupId: string) =>
    products.filter(
      (p) =>
        groupId === "all" ||
        p.category?._id === groupId ||
        p.subcategories?.some((sc) => String(sc._id) === groupId),
    ).length;

  const getSubcategoryCount = (groupId: string, subId: string) =>
    products.filter(
      (p) =>
        (p.category?._id === groupId ||
          p.subcategories?.some((sc) => String(sc._id) === groupId)) &&
        p.subcategories?.some((sc) => String(sc._id) === subId),
    ).length;
  const getPriceCount = (rangeId: string) =>
    products.filter((p) => matchesPriceRange(effectivePrice(p), rangeId))
      .length;

  const categoryOptions = [
    { id: "all", label: "All Products", count: products.length },
    ...(useDepartments
      ? departments.map((d) => ({
          id: d.id,
          label: d.label,
          count: countForGroup(d.id),
        }))
      : categories.map((c) => ({
          id: c._id,
          label: c.name,
          count: countForGroup(c._id),
        }))),
  ];

  const activeCatLabel =
    categoryOptions.find((c) => c.id === activeCategory)?.label ?? "";
  const activeCatCount =
    categoryOptions.find((c) => c.id === activeCategory)?.count ?? 0;
  const subcats: Category[] =
    activeCategory === "all"
      ? []
      : useDepartments
        ? (departments.find((d) => d.id === activeCategory)?.leaves ?? []).map(
            (l) => ({
              _id: l.id,
              name: l.label,
              slug: l.id,
            }),
          )
        : Array.from(subcategoryMap.get(activeCategory)?.values() ?? []);

  const labelFor = (id: string) =>
    curatorItems.find((c) => c.id === id)?.label ??
    PRICE_RANGES.find((p) => p.id === id)?.label ??
    id;
  const activeChips = [...curators, ...priceRanges];
  const filterCount = activeChips.length;

  const groupOptions = categoryOptions.filter((c) => c.id !== "all");

  /**
   * Tiles come from the CANONICAL tree, not portal-api's.
   *
   * Two reasons. Artwork lives on canonical nodes, and portal-api simply does
   * not have some of these categories — Anuttama's 18 bars are one portal-api
   * subcategory but five canonical ones (Plain Dark, Nut, Fruit, Flavoured,
   * Milk). Filtering still works because each node ships the ids of its own
   * products, so nothing has to be expressible as a portal-api id.
   *
   * Level choice: show the deepest level that actually offers a choice. A store
   * with several L1s tiles those; a single-line store like Gramiyaa (all
   * "Grocery & Staples") drops to its children, where the real distinctions
   * are (Coconut Oil, Mustard Oil, Sesame Oil…).
   */
  /**
   * Drill-down state: the chain of categories the shopper has opened.
   * Empty = showing the top level.
   */
  const [drill, setDrill] = useState<CanonicalNode[]>([]);

  // Reset the drill whenever the catalogue underneath changes (outlet switch).
  useEffect(() => {
    setDrill([]);
  }, [canonical]);

  /**
   * The level currently on screen: the children of whatever we last opened, or
   * the top of the tree.
   *
   * One adjustment at the root — if a store has a single top category, showing
   * it alone is a dead end, so we start one level in. Gramiyaa is entirely
   * "Grocery & Staples"; its shoppers want the oils.
   */
  const currentLevel: CanonicalNode[] = useMemo(() => {
    if (drill.length) return drill[drill.length - 1].children ?? [];
    let level = canonical;
    for (
      let d = 0;
      d < 3 && level.length === 1 && level[0].children?.length;
      d++
    ) {
      level = level[0].children!;
    }
    return level;
  }, [canonical, drill]);

  const tileSource = useMemo(
    () =>
      currentLevel.map((n) => ({
        id: n._id,
        label: n.name,
        count: n.productCount ?? n.productIds?.length ?? 0,
        image: n.display?.tileImageUrl,
        productIds: n.productIds ?? [],
        node: n,
        hasChildren: (n.children?.length ?? 0) > 0,
      })),
    [currentLevel],
  );

  const illustrated = tileSource.filter((c) => c.image);
  // Inside a drill-down a single child is still worth showing — it tells the
  // shopper where they are. Only the TOP level needs more than one choice to
  // justify appearing at all.
  const enoughChoices =
    drill.length > 0 ? tileSource.length > 0 : tileSource.length > 1;
  /**
   * A partly-illustrated level still gets the row.
   *
   * Demanding artwork on EVERY sibling meant one bare category hid the whole
   * row, so a taxonomy midway through being illustrated looked identical to one
   * with no art at all — which is exactly how the canonical tree ships today.
   * A majority reads as a picture row; the stragglers fall back to a monogram
   * on the same peach the artwork is shot on, so they recede instead of
   * punching a hole in the row.
   */
  const useTiles = enoughChoices && illustrated.length * 2 >= tileSource.length;

  /**
   * Whether a category control is worth showing at all.
   *
   * A single-product-line store ends up with "All Products 18" beside
   * "Snacks 18" — two controls selecting the identical set. That is not a
   * filter, it is furniture. Fewer than two real choices, or one choice that
   * already holds everything, means there is nothing to choose between.
   */
  const legacyRowIsUseful =
    groupOptions.length > 1 &&
    !groupOptions.every((c) => c.count >= products.length);
  const categoryRowIsUseful = useTiles || legacyRowIsUseful;

  /** Open a category: filter to it, and step into it when it has children. */
  const openTile = (t: (typeof tileSource)[number]) => {
    handleCategoryWithIds(t.id, t.productIds);
    if (t.hasChildren) setDrill((d) => [...d, t.node]);
  };

  /** Step back out to `depth` levels of drill (0 = the top). */
  const drillTo = (depth: number) => {
    const next = drill.slice(0, depth);
    setDrill(next);
    if (next.length) {
      const parent = next[next.length - 1];
      handleCategoryWithIds(parent._id, parent.productIds ?? []);
    } else {
      handleCategory("all");
    }
  };

  // Still no "All" tile — a synthetic swatch standing for the whole catalogue is
  // a different thing from a real category that is merely waiting on its art.
  const tiles = useTiles ? tileSource : [];

  /**
   * What the header reports on the right. At the top level that is the whole
   * catalogue (each product sits in exactly one leaf, so the tile counts sum);
   * inside a drill it is the node you opened.
   */
  const browseCount = drill.length
    ? (drill[drill.length - 1].productCount ??
      drill[drill.length - 1].productIds?.length ??
      0)
    : tileSource.reduce((n, t) => n + t.count, 0);

  // Remounts the row on every level change so the entrance animation replays and
  // the shopper sees the new level arrive rather than silently swap.
  const levelKey = drill.map((n) => n._id).join("/") || "root";

  /* Inline rather than a styled-jsx class: this component already ships two
     style blocks, and scoped-class resolution across them is exactly the kind of
     thing that silently stops applying after a refactor. An inline style either
     works or is visible in the DOM. */
  return (
    <div style={serviceBlocked ? { position: "relative" } : undefined}>
      {serviceBlocked && serviceArea && (
        /* Transparent catcher over the whole section. Keyboard users reach it in
           tab order and get the same message; the tree below is inert. */
        <button
          type="button"
          aria-label={serviceArea.blockMessage}
          onClick={() => toast.error(serviceArea.blockMessage)}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            width: "100%",
            height: "100%",
            background: "transparent",
            border: "none",
            cursor: "not-allowed",
          }}
        />
      )}
      <div
        className="gl-cfb"
        aria-disabled={serviceBlocked || undefined}
        style={
          serviceBlocked
            ? {
                pointerEvents: "none",
                opacity: 0.45,
                filter: "grayscale(1)",
                userSelect: "none",
              }
            : undefined
        }
      >
        {/* ── Browse: header + artwork tiles ── */}
        {tiles.length > 0 && (
          <section className="gl-cfb-browse" aria-label="Shop by category">
            {/*
            One header carries everything the shopper needs to stay oriented:
            what this row is, where they are, and how much is in scope. It is
            rendered at every level — including the top, where the trail is just
            "All categories" — so opening a category never shifts the tiles.
          */}
            <div className="gl-cfb-head">
              <div className="gl-cfb-head-main">
                <span className="gl-cfb-eyebrow">Browse</span>
                <nav className="gl-cfb-crumbs" aria-label="Category path">
                  {drill.length === 0 ? (
                    <strong>All categories</strong>
                  ) : (
                    <button onClick={() => drillTo(0)}>All categories</button>
                  )}
                  {drill.map((n, i) => (
                    <React.Fragment key={n._id}>
                      <span className="gl-cfb-crumb-sep" aria-hidden="true">
                        ›
                      </span>
                      {i === drill.length - 1 ? (
                        <strong aria-current="page">{n.name}</strong>
                      ) : (
                        <button onClick={() => drillTo(i + 1)}>{n.name}</button>
                      )}
                    </React.Fragment>
                  ))}
                </nav>
              </div>
              <span className="gl-cfb-head-count">
                {browseCount} item{browseCount === 1 ? "" : "s"}
              </span>
            </div>

            <div className="gl-cfb-tiles" key={levelKey}>
              {tiles.map((t, i) => {
                const on = activeCategory === t.id;
                // A tile either opens a level or filters to a leaf — never both in
                // the shopper's mind, because an opened tile is replaced by its
                // children and so is never on screen in a selected state.
                const label = t.hasChildren
                  ? `${t.label}, ${t.count} item${t.count === 1 ? "" : "s"}, open`
                  : `${t.label}, ${t.count} item${t.count === 1 ? "" : "s"}`;
                return (
                  <button
                    key={t.id}
                    {...(t.hasChildren ? {} : { "aria-pressed": on })}
                    aria-label={label}
                    onClick={() =>
                      on && !t.hasChildren ? drillTo(drill.length) : openTile(t)
                    }
                    className={`gl-cfb-tile${on ? " on" : ""}`}
                    style={{ animationDelay: `${Math.min(i, 9) * 28}ms` }}
                  >
                    <span className="gl-cfb-tile-img">
                      {t.image ? (
                        /* eslint-disable-next-line @next/next/no-img-element */
                        <img
                          src={t.image}
                          alt=""
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <span className="gl-cfb-tile-mono" aria-hidden="true">
                          {t.label.trim().charAt(0).toUpperCase()}
                        </span>
                      )}
                    </span>
                    <span className="gl-cfb-tile-label">
                      <span className="gl-cfb-tile-name">
                        {t.label}
                        {/* Same glyph as the breadcrumb separator: in this bar "›"
                          always means "a level deeper". */}
                        {t.hasChildren && (
                          <span className="gl-cfb-tile-into" aria-hidden="true">
                            {" "}
                            ›
                          </span>
                        )}
                      </span>
                      <span className="gl-cfb-tile-count">{t.count}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Category pills ──
          Suppressed when the tile row is up: both render the same groups, and
          stacking them repeated every label twice. The pills remain the
          fallback whenever a catalogue has no artwork. */}
        {!useTiles && categoryRowIsUseful && (
          <div
            className="gl-cfb-scroll"
            role="tablist"
            aria-label="Product categories"
          >
            {categoryOptions.map((cat) => {
              const on = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  role="tab"
                  aria-selected={on}
                  onClick={() => handleCategory(cat.id)}
                  className={`gl-cfb-pill${on ? " on" : ""}`}
                >
                  {cat.label}
                  <span className="gl-cfb-count">{cat.count}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* ── Contextual subcategory row ── */}
        {/* Same rule as the category row: a lone leaf holding the whole category
          ("All Snacks 18 | Chocolates 18") is not a choice. */}
        {subcats.length > 1 && (
          <div className="gl-cfb-scroll gl-cfb-subrow">
            <span className="gl-cfb-sublabel">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 6h16M7 12h13M10 18h10" />
                <circle cx="4" cy="12" r="0.6" />
                <circle cx="7" cy="18" r="0.6" />
              </svg>
              In {activeCatLabel}
            </span>
            <button
              onClick={() => handleSubcategory("all")}
              className={`gl-cfb-sub${activeSubcategory === "all" ? " on" : ""}`}
            >
              All {activeCatLabel}{" "}
              <span className="gl-cfb-count">{activeCatCount}</span>
            </button>
            {subcats.map((sc) => (
              <button
                key={sc._id}
                onClick={() => handleSubcategory(sc._id)}
                className={`gl-cfb-sub${activeSubcategory === sc._id ? " on" : ""}`}
              >
                {sc.name}{" "}
                <span className="gl-cfb-count">
                  {getSubcategoryCount(activeCategory, sc._id)}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* ── Filter toolbar ── */}
        <div className="gl-cfb-toolbar">
          <div className="gl-cfb-dd" ref={curatorRef}>
            <button
              className={`gl-cfb-ddbtn${curators.length ? " active" : ""}`}
              onClick={() =>
                setOpenMenu(openMenu === "curator" ? null : "curator")
              }
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3l2.5 5 5.5.8-4 3.9 1 5.5L12 16l-5 2.6 1-5.5-4-3.9 5.5-.8z" />
              </svg>
              Curator&apos;s pick
              {curators.length > 0 && (
                <span className="gl-cfb-badge">{curators.length}</span>
              )}
              <Chevron open={openMenu === "curator"} />
            </button>
            {openMenu === "curator" && (
              <div className="gl-cfb-pop">
                <div className="gl-cfb-pop-head">Curator&apos;s pick</div>
                <div className="gl-cfb-pop-body">
                  {curatorItems.map((c) => (
                    <label key={c.id} className="gl-cfb-check">
                      <input
                        type="checkbox"
                        checked={curators.includes(c.id)}
                        onChange={() => toggleCurator(c.id)}
                      />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="gl-cfb-dd" ref={priceRef}>
            <button
              className={`gl-cfb-ddbtn${priceRanges.length ? " active" : ""}`}
              onClick={() => setOpenMenu(openMenu === "price" ? null : "price")}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 11.5V4.5A1.5 1.5 0 0 1 4.5 3h7L20.5 12l-8.5 8.5z" />
                <circle cx="7.8" cy="7.8" r="1.3" />
              </svg>
              Price
              {priceRanges.length > 0 && (
                <span className="gl-cfb-badge">{priceRanges.length}</span>
              )}
              <Chevron open={openMenu === "price"} />
            </button>
            {openMenu === "price" && (
              <div className="gl-cfb-pop gl-cfb-pop--right">
                <div className="gl-cfb-pop-head">Price range</div>
                <div className="gl-cfb-pop-body">
                  {PRICE_RANGES.map((r) => (
                    <label key={r.id} className="gl-cfb-check">
                      <input
                        type="checkbox"
                        checked={priceRanges.includes(r.id)}
                        onChange={() => togglePrice(r.id)}
                      />
                      <span>{r.label}</span>
                      <em className="gl-cfb-count">{getPriceCount(r.id)}</em>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Active chips */}
          {filterCount > 0 && (
            <div className="gl-cfb-chips">
              {activeChips.map((id) => (
                <button
                  key={id}
                  className="gl-cfb-chip"
                  onClick={() =>
                    curators.includes(id) ? toggleCurator(id) : togglePrice(id)
                  }
                >
                  {labelFor(id)}
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  >
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              ))}
              <button className="gl-cfb-clear" onClick={clearAll}>
                Clear all
              </button>
            </div>
          )}
        </div>

        <style>{`
        .gl-cfb{display:flex;flex-direction:column;gap:14px}
        /* min-width:0 + max-width:100% keep this flex item from expanding to its
           content width on old WebViews, which resolve a flex item's min-width:auto
           to min-content (~content width) instead of 0. Without it the pill row blows
           out of .gl-container and forces a horizontal scroll on the whole page. */
        /* overflow-x:auto forces overflow-y to clip (per spec), so the strip cuts
           anything that reaches its top/bottom edge. The vertical padding gives the
           pill hover-lift (translateY(-1px)) and keyboard focus rings room so their
           top edge isn't sliced off. */
        .gl-cfb-scroll{display:flex;align-items:center;gap:9px;overflow-x:auto;min-width:0;max-width:100%;scrollbar-width:none;-webkit-overflow-scrolling:touch;padding-top:4px;padding-bottom:4px}
        .gl-cfb-scroll::-webkit-scrollbar{display:none}
        .gl-cfb-pill{flex-shrink:0;display:inline-flex;align-items:center;gap:7px;font-family:var(--font-body);font-size:14px;font-weight:600;
          color:var(--techno-black);background:var(--surface);border:1px solid var(--border-strong,var(--border));border-radius:999px;
          padding:10px 16px;cursor:pointer;transition:all .18s}
        .gl-cfb-pill:hover{border-color:var(--navy);color:var(--navy);transform:translateY(-1px)}
        .gl-cfb-pill.on{background:var(--techno-black);color:#fff;border-color:var(--techno-black)}
        .gl-cfb-count{font-size:11.5px;font-weight:700;opacity:.55}
        .gl-cfb-pill.on .gl-cfb-count{opacity:.7}

        .gl-cfb-subrow{padding-top:2px}
        .gl-cfb-sublabel{flex-shrink:0;display:inline-flex;align-items:center;gap:7px;font-size:11.5px;font-weight:700;letter-spacing:.08em;
          text-transform:uppercase;color:var(--coral-deep);margin-right:4px}
        .gl-cfb-sub{flex-shrink:0;display:inline-flex;align-items:center;gap:6px;font-size:13px;font-weight:600;color:var(--techno-black-70);
          background:var(--surface);border:1px solid var(--border-soft);border-radius:999px;padding:8px 14px;cursor:pointer;transition:all .18s}
        .gl-cfb-sub:hover{border-color:var(--coral);color:var(--coral-deep)}
        .gl-cfb-sub.on{background:var(--coral);color:#fff;border-color:var(--coral)}
        .gl-cfb-sub.on .gl-cfb-count{opacity:.8}

        .gl-cfb-toolbar{display:flex;align-items:center;flex-wrap:wrap;gap:10px;padding-top:4px;border-top:1px solid var(--border-soft);margin-top:2px;padding-top:14px}
        .gl-cfb-dd{position:relative}
        .gl-cfb-ddbtn{display:inline-flex;align-items:center;gap:8px;font-family:var(--font-body);font-size:13.5px;font-weight:600;color:var(--techno-black);
          background:var(--surface);border:1px solid var(--border-strong,var(--border));border-radius:12px;padding:9px 14px;cursor:pointer;transition:all .18s}
        .gl-cfb-ddbtn:hover{border-color:var(--navy)}
        .gl-cfb-ddbtn.active{border-color:var(--coral);color:var(--coral-deep);background:var(--coral-tint)}
        .gl-cfb-ddbtn svg{color:var(--navy)}
        .gl-cfb-ddbtn.active svg{color:var(--coral)}
        .gl-cfb-badge{display:inline-grid;place-items:center;min-width:18px;height:18px;padding:0 5px;border-radius:999px;background:var(--coral);
          color:#fff;font-size:11px;font-weight:700}
        .gl-cfb-pop{position:absolute;top:calc(100% + 8px);left:0;z-index:40;min-width:min(230px,calc(100vw - 32px));max-width:calc(100vw - 32px);background:var(--surface);
          border:1px solid var(--border-soft);border-radius:16px;box-shadow:var(--shadow-float);overflow:hidden;animation:fadeUp 150ms ease}
        /* On phones the trigger can sit near the right edge; open this popover
           inward (anchored to the trigger's right) so it never spills off-screen. */
        @media (max-width:600px){.gl-cfb-pop--right{right:0;left:auto}}
        .gl-cfb-pop-head{font-family:var(--font-display);font-weight:600;font-size:15px;padding:14px 16px 8px}
        .gl-cfb-pop-body{padding:4px 8px 10px;max-height:280px;overflow-y:auto}
        .gl-cfb-check{display:flex;align-items:center;gap:10px;padding:9px 10px;border-radius:10px;font-size:14px;color:var(--techno-black-70);cursor:pointer;transition:background .15s}
        .gl-cfb-check:hover{background:var(--cream)}
        .gl-cfb-check input{width:17px;height:17px;cursor:pointer;accent-color:var(--coral)}
        .gl-cfb-check span{flex:1}
        .gl-cfb-check em{font-style:normal}

        /* ── Browse ──────────────────────────────────────────────────────────
           The category nav when we can illustrate every group. One header plus
           one row of tiles: the header answers "where am I / how much is here",
           the tiles answer "where can I go". Nothing else competes. */
        .gl-cfb-browse{display:flex; flex-direction:column}

        .gl-cfb-head{
          display:flex; align-items:flex-end; justify-content:space-between; gap:16px;
          margin:0 0 16px;
        }
        .gl-cfb-head-main{display:flex; flex-direction:column; gap:5px; min-width:0}
        .gl-cfb-eyebrow{
          font-size:10.5px; font-weight:700; letter-spacing:.16em; text-transform:uppercase;
          color:var(--techno-black-50,#8A8579);
        }
        /* Always rendered — at the top level the trail is just "All categories" —
           so opening a category never nudges the tiles down the page. */
        .gl-cfb-crumbs{
          display:flex; align-items:center; gap:6px; flex-wrap:wrap; margin:0;
          font-family:var(--font-display); font-size:17px; line-height:1.2;
        }
        .gl-cfb-crumbs strong{color:var(--techno-black,#1D1D1D); font-weight:600}
        .gl-cfb-crumbs button{
          background:none; border:0; cursor:pointer; font:inherit;
          color:var(--techno-black-50,#8A8579); font-weight:500;
          /* Negative margin keeps the enlarged hit area from changing the layout. */
          padding:6px 5px; margin:-6px -5px; border-radius:8px; transition:color .15s;
        }
        .gl-cfb-crumbs button:hover{color:var(--techno-black,#1D1D1D)}
        .gl-cfb-crumb-sep{color:var(--techno-black-50,#8A8579); opacity:.5; font-size:15px}
        .gl-cfb-head-count{
          flex-shrink:0; font-size:12.5px; font-weight:500; color:var(--techno-black-50,#8A8579);
          font-variant-numeric:tabular-nums; padding-bottom:2px;
        }

        .gl-cfb-tiles{
          display:flex; gap:22px; overflow-x:auto;
          /* This is the scroll container, so anything outside its padding box is
             clipped — and every decoration on a tile sits OUTSIDE it: the
             selected ring 4.5px, the hover shadow 6px, the focus ring 5px. At
             2px of side padding the first tile's ring was sheared off flat down
             its left edge, and at 4px of top padding it was shaved along the
             top. Top needs the most room, because the tile also lifts 4px on
             hover — putting the ring 8.5px above the tile, or 9px when it is
             focus-ringed at the same time.

             The negative margin cancels the padding again, so the row lands
             exactly where it did before and the tiles stay optically flush with
             the heading rather than stepping in. */
          padding:12px 8px 24px; margin:-8px -8px 0;
          align-items:flex-start; scrollbar-width:none; -ms-overflow-style:none;
          /* Without this a flex scroller refuses to shrink below its content and
             pushes the whole page sideways on older WebViews. */
          min-width:0;
        }
        .gl-cfb-tiles::-webkit-scrollbar{display:none}
        .gl-cfb-tile{
          flex:0 0 auto; width:96px; background:none; border:0; padding:0; cursor:pointer;
          display:flex; flex-direction:column; align-items:center; gap:10px;
          font:inherit; color:var(--techno-black,#1D1D1D);
          animation:glTileIn 340ms cubic-bezier(.22,.61,.36,1) both;
        }
        @keyframes glTileIn{from{opacity:0; transform:translateY(10px)} to{opacity:1; transform:none}}
        .gl-cfb-tile:focus-visible{outline:none}
        .gl-cfb-tile:focus-visible .gl-cfb-tile-img{
          box-shadow:0 0 0 3px var(--cream,#FBF7F0), 0 0 0 5px var(--navy,#1F3B5B);
        }
        .gl-cfb-tile-img{
          width:96px; height:96px; border-radius:24px; overflow:hidden; display:block;
          /* Matches the peach the artwork was shot on, so the tile reads as one
             object instead of a pale square floating on the cream page. The
             gradient and the inset hairline stop it reading as flat fill. */
          background:linear-gradient(160deg,#FCF2E8 0%,#F4DFCB 100%);
          box-shadow:inset 0 0 0 1px rgba(29,29,29,.05);
          transition:transform 220ms cubic-bezier(.22,.61,.36,1), box-shadow 220ms ease;
        }
        .gl-cfb-tile-img img{
          width:100%; height:100%; object-fit:cover; display:block;
          /* The artwork is composed with a wide margin, which left the subject
             looking lost at tile size. Crop in so it fills. */
          transform:scale(1.18);
        }
        /* Stand-in for a category whose artwork has not landed yet. Sits on the
           tile's own peach gradient and is deliberately low-contrast, so a gap
           in the taxonomy reads as a quiet tile rather than a broken one. */
        .gl-cfb-tile-mono{
          width:100%; height:100%; display:flex; align-items:center; justify-content:center;
          font-size:34px; font-weight:600; letter-spacing:-.02em;
          color:rgba(29,29,29,.22);
        }
        .gl-cfb-tile:hover .gl-cfb-tile-img{
          transform:translateY(-4px);
          box-shadow:inset 0 0 0 1px rgba(29,29,29,.05), 0 14px 28px -8px rgba(29,29,29,.22);
        }
        .gl-cfb-tile:active .gl-cfb-tile-img{transform:translateY(-1px) scale(.97)}
        /* Selection is a leaf-only state: a tile with children is replaced by
           those children the moment it is opened, so it is never on screen
           selected. A layered ring reads richer than one heavy stroke. */
        .gl-cfb-tile.on .gl-cfb-tile-img{
          box-shadow:inset 0 0 0 1px rgba(29,29,29,.05),
                     0 0 0 3px var(--cream,#FBF7F0),
                     0 0 0 4.5px var(--coral,#F26250),
                     0 10px 24px -10px rgba(242,98,80,.5);
        }
        .gl-cfb-tile-label{
          display:flex; flex-direction:column; align-items:center; gap:3px; text-align:center;
        }
        .gl-cfb-tile-name{
          font-size:13px; line-height:1.3; font-weight:500; letter-spacing:-.006em;
          color:var(--techno-black-70,#4A4740);
          /* Two lines reserved whether the name needs them or not, so every
             count in the row sits on the same baseline. */
          min-height:34px;
          display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden;
        }
        .gl-cfb-tile-into{color:var(--techno-black-50,#8A8579); font-weight:600}
        .gl-cfb-tile-count{
          font-size:11px; font-weight:500; color:var(--techno-black-50,#8A8579);
          font-variant-numeric:tabular-nums;
        }
        .gl-cfb-tile.on .gl-cfb-tile-name{color:var(--techno-black,#1D1D1D); font-weight:650}
        .gl-cfb-tile.on .gl-cfb-tile-count{color:var(--coral-deep,#D94A39)}

        @media (max-width:640px){
          .gl-cfb-tiles{gap:16px; padding-bottom:20px}
          .gl-cfb-tile,.gl-cfb-tile-img{width:80px}
          .gl-cfb-tile-img{height:80px; border-radius:20px}
          .gl-cfb-crumbs{font-size:15px}
          .gl-cfb-tile-name{font-size:12.5px; min-height:32px}
          .gl-cfb-tile-mono{font-size:28px}
        }
        @media (prefers-reduced-motion:reduce){
          .gl-cfb-tile{animation:none}
          .gl-cfb-tile-img{transition:box-shadow 220ms ease}
          .gl-cfb-tile:hover .gl-cfb-tile-img,
          .gl-cfb-tile:active .gl-cfb-tile-img{transform:none}
        }

        .gl-cfb-chips{display:flex;align-items:center;flex-wrap:wrap;gap:8px}
        .gl-cfb-chip{display:inline-flex;align-items:center;gap:6px;font-size:12.5px;font-weight:600;color:var(--coral-deep);
          background:var(--coral-tint);border:1px solid var(--coral-soft);border-radius:999px;padding:6px 11px;cursor:pointer;transition:all .15s}
        .gl-cfb-chip:hover{background:var(--coral-soft)}
        .gl-cfb-clear{font-size:13px;font-weight:600;color:var(--techno-black-50);background:none;border:none;cursor:pointer;text-decoration:underline;text-underline-offset:3px;padding:4px}
        .gl-cfb-clear:hover{color:var(--techno-black)}
      `}</style>
      </div>
    </div>
  );
};
